import React, { useEffect, useState } from "react";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { useNavigate, useParams } from "react-router-dom";

import styled, { createGlobalStyle } from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Text from "components/atoms/text/Text";

import PDFChatPageHeader from "components/organisms/review/PDFChatPageHeader";
import PDFChatPageLayout from "components/organisms/review/PDFChatPageLayout";
import PDFChatSidebar from "components/organisms/review/PDFChatSidebar";

import { useBibliography } from "hooks/queries/useBibliographies";
import { useReviewPage } from "hooks/queries/useReview";
import { useUser } from "hooks/queries/useUser";
import { usePDFChatWebSocket } from "hooks/usePDFChatWebSocket";

import Constants from "utils/constants";

const PageLayout = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
`;

const PdfReaderContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const PdfReaderToolbar = styled.div`
    width: 100%;
    min-height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    padding: 0 8px;
    gap: 8px;
    flex-shrink: 0;
`;

const PageMoveControlContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 150px;
`;

const PageMoveInputControl = styled.input`
    width: 30px;
    text-align: center;
    font-size: 1.2rem;
    border: none;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

const PageMoveText = styled(Text)`
    font-size: 1.2rem;
`;

const PageMoveButton = styled.button`
    height: 20px;
    min-width: 20px;
    padding: 0 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1.2rem;
    &:hover {
        background: #e0e0e0;
    }
`;

const PageImageContainer = styled.div`
    position: relative;
    width: 100%;
    flex: 1;
    overflow: auto;
    cursor: crosshair;
`;

// 리뷰 박스 리사이즈 스타일
const ResizableHandleStyle = createGlobalStyle`
  .resizable-hovered .react-resizable-handle {
    opacity: 1;
    transition: opacity 0.2s;
  }
  .react-resizable-handle {
    opacity: 0;
    transition: opacity 0.2s;
  }
`;

const PageImage = styled.img`
    width: 100%;
    display: block;
`;

const ActiveDimLayer = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;
    z-index: 10;
`;

const DimOverlay = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 11;
`;

const HighlightBox = styled.div`
    position: absolute;
    left: ${({ left }) => left}px;
    top: ${({ top }) => top}px;
    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;
    background: rgba(255, 255, 0, 0.3);
    border: 2px solid #ffd600;
    z-index: 12;
    pointer-events: none;
`;

const HightlightBoxButtonGroup = styled.div`
    position: absolute;
    right: ${({ right }) => right}px;
    top: ${({ top }) => top}px;
    z-index: 21;

    display: flex;
    gap: 2px;
`;

const HighlightBoxButtonText = styled(Text)`
    padding: 2px 4px;

    cursor: pointer;
    color: var(--color-White);
    background-color: var(--color-Warn);
    border-radius: 2px;

    &:hover {
        background-color: var(--color-WarnHover);
    }
`;

function ReferenceReviewPage(props) {
    // 라우터 파라미터
    const { space_uuid, reference_no, reference_file_uuid } = useParams();
    const navigate = useNavigate();
    const referenceNo = Number(reference_no);

    // 사용자 데이터
    const [user, setUser] = useState({});
    const userQuery = useUser();

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    // 레퍼런스 데이터
    const referenceQuery = useBibliography({ reference_no: referenceNo });
    const [reference, setReference] = useState({});
    useEffect(() => {
        if (referenceQuery.data) {
            setReference(referenceQuery.data);
        }
    }, [referenceQuery.data]);

    useEffect(() => {
        if (!Number.isInteger(referenceNo) || referenceNo < 1) {
            navigate("/error", { replace: true });
        }
    }, [referenceNo, navigate]);

    // region: page 이동 처리
    const [curPage, setCurPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [inputPage, setInputPage] = useState(curPage);

    const [orgPageImage, setOrgPageImage] = useState("");
    const [orgPageImageSize, setOrgPageImageSize] = useState({ width: 1, height: 1 });
    const [curPageImageSize, setCurPageImageSize] = useState({ width: 1, height: 1 });
    const imgRef = React.useRef(null);

    // 페이지 캐시 (이미지/박스 미리 저장)
    // {
    //   [pageNumber]: {
    //      imgSrc: string,
    //      imageSize: { width, height },
    //      boxes: [...]
    //   }
    // }
    const [pageCache, setPageCache] = useState({});

    // 메인(현재) 페이지
    const reviewPageQuery = useReviewPage({
        reference_file_uuid: reference_file_uuid,
        page: curPage,
    });

    // 이전/다음 페이지 미리 불러오기용 쿼리
    const prevPage = curPage > 1 ? curPage - 1 : null;
    const nextPage = curPage < maxPage ? curPage + 1 : null;

    const prevPageQuery = useReviewPage({
        reference_file_uuid: reference_file_uuid,
        page: prevPage ?? curPage, // prevPage 없을 때는 의미 없는 값
    });

    const nextPageQuery = useReviewPage({
        reference_file_uuid: reference_file_uuid,
        page: nextPage ?? curPage,
    });

    // 현재 페이지 응답 처리 + 캐시에 저장
    useEffect(() => {
        if (reviewPageQuery.data) {
            const { image, boxes: pageBoxes, max_page, page } = reviewPageQuery.data;
            const imgSrc = "data:image/png;base64," + image.base64;

            setOrgPageImage(imgSrc);
            setOrgPageImageSize({ width: image.width, height: image.height });
            setBoxes(pageBoxes);
            setMaxPage(max_page);

            const pageNo = page ?? curPage;
            setPageCache((prev) => ({
                ...prev,
                [pageNo]: {
                    imgSrc,
                    imageSize: { width: image.width, height: image.height },
                    boxes: pageBoxes,
                },
            }));
        }
    }, [reviewPageQuery.data, curPage]);

    // 이전/다음 페이지도 캐시에 저장해서 prefetch
    useEffect(() => {
        if (prevPageQuery.data) {
            const { image, boxes: pageBoxes, page } = prevPageQuery.data;
            const imgSrc = "data:image/png;base64," + image.base64;
            const pageNo = page ?? prevPage;

            if (!pageNo) return;
            setPageCache((prev) => ({
                ...prev,
                [pageNo]: {
                    imgSrc,
                    imageSize: { width: image.width, height: image.height },
                    boxes: pageBoxes,
                },
            }));
        }
    }, [prevPageQuery.data, prevPage]);

    useEffect(() => {
        if (nextPageQuery.data) {
            const { image, boxes: pageBoxes, page } = nextPageQuery.data;
            const imgSrc = "data:image/png;base64," + image.base64;
            const pageNo = page ?? nextPage;

            if (!pageNo) return;
            setPageCache((prev) => ({
                ...prev,
                [pageNo]: {
                    imgSrc,
                    imageSize: { width: image.width, height: image.height },
                    boxes: pageBoxes,
                },
            }));
        }
    }, [nextPageQuery.data, nextPage]);

    // 페이지 변경 시 input 동기화 + 현재/이전/다음 페이지 refetch
    useEffect(() => {
        setInputPage(curPage);
        reviewPageQuery.refetch({
            reference_file_uuid: reference_file_uuid,
            page: curPage,
        });

        if (prevPage) {
            prevPageQuery.refetch({
                reference_file_uuid: reference_file_uuid,
                page: prevPage,
            });
        }
        if (nextPage) {
            nextPageQuery.refetch({
                reference_file_uuid: reference_file_uuid,
                page: nextPage,
            });
        }
    }, [reference_file_uuid, curPage]);

    const handleInputPageChanged = (newPage, scrollDirection = null) => {
        if (newPage < 1) newPage = 1;
        if (newPage > maxPage) newPage = maxPage;
        if (newPage === curPage) return;

        // 스크롤로 페이지를 넘긴 경우에만 방향 플래그 저장
        scrollPageDirectionRef.current = scrollDirection;

        const cached = pageCache[newPage];
        if (cached) {
            setOrgPageImage(cached.imgSrc);
            setOrgPageImageSize(cached.imageSize);
            setBoxes(cached.boxes);
        }

        setCurPage(newPage);
    };

    // 스크롤로 페이지 넘기기
    const scrollContainerRef = React.useRef(null); // 스크롤 컨테이너 ref
    const wheelCooldownRef = React.useRef(0);
    const scrollPageDirectionRef = React.useRef(null); // 'next' | 'prev' | null
    const handleWheelOnContainer = (e) => {
        const container = e.currentTarget;
        const atTop = container.scrollTop <= 0;
        const atBottom = Math.ceil(container.scrollTop + container.clientHeight) >= container.scrollHeight;

        const now = Date.now();
        const cooldown = 1000;
        if (now - wheelCooldownRef.current < cooldown) return;

        // 위로 스크롤 & 맨 위 -> 이전 페이지
        if (e.deltaY < 0 && atTop && curPage > 1) {
            handleInputPageChanged(curPage - 1, "prev");
            wheelCooldownRef.current = now;
            return;
        }

        // 아래로 스크롤 & 맨 아래 -> 다음 페이지
        if (e.deltaY > 0 && atBottom && curPage < maxPage) {
            handleInputPageChanged(curPage + 1, "next");
            wheelCooldownRef.current = now;
            return;
        }
    };

    const handlePageImageLoad = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const direction = scrollPageDirectionRef.current;

        if (direction === "next") {
            // 다음 페이지: 맨 위로
            container.scrollTop = 0;
        } else if (direction === "prev") {
            // 이전 페이지: 맨 아래로
            container.scrollTop = container.scrollHeight - container.clientHeight;
        } else {
            // 기타 (버튼/입력 등): 기본적으로 맨 위로
            container.scrollTop = 0;
        }

        // 사용 후 플래그 초기화
        scrollPageDirectionRef.current = null;
    };

    // region: 리뷰 박스 처리
    const [boxes, setBoxes] = useState([]);
    const [hoveredBoxIdx, setHoveredBoxIdx] = useState(null);
    const [isResizing, setIsResizing] = useState(false);

    // 이미지 크기 변화에 따라 박스 크기 변화시키는 리사이저 등록
    useEffect(() => {
        if (!imgRef.current) return;

        // ResizeObserver 인스턴스 생성
        const observer = new window.ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === imgRef.current) {
                    setCurPageImageSize({
                        width: entry.contentRect.width,
                        height: entry.contentRect.height,
                    });
                }
            }
        });

        observer.observe(imgRef.current);

        // 초기값 세팅
        setCurPageImageSize({
            width: imgRef.current.offsetWidth,
            height: imgRef.current.offsetHeight,
        });

        // 언마운트 시 해제
        return () => {
            observer.disconnect();
        };
    }, [orgPageImage]);

    // 박스 리사이즈 서버 반영
    const resizeBoxOnServer = async (box, idx) => {
        // 서버 반영 로직 (필요시 구현)
    };

    // 박스 선택 관련
    const [selectedBox, setSelectedBox] = useState(null);
    const [searchBox, setSearchBox] = useState(null);

    // 박스 렌더링
    const renderBoxes = () =>
        boxes.map((box, idx) => {
            const [x1, y1, x2, y2] = box.data.bbox;
            const left = x1 * curPageImageSize.width;
            const top = y1 * curPageImageSize.height;
            const width = (x2 - x1) * curPageImageSize.width;
            const height = (y2 - y1) * curPageImageSize.height;
            const isHovered = hoveredBoxIdx === idx;

            const buttonGroupRight = (1 - x2) * curPageImageSize.width;
            const buttonGroupTop = top + height;

            const handleResize = (event, { size, handle }) => {
                let newX1 = x1,
                    newY1 = y1;
                if (handle.includes("w")) newX1 = (left + (width - size.width)) / curPageImageSize.width;
                if (handle.includes("n")) newY1 = (top + (height - size.height)) / curPageImageSize.height;
                const newX2 = newX1 + size.width / curPageImageSize.width;
                const newY2 = newY1 + size.height / curPageImageSize.height;
                setBoxes((prev) => {
                    const newBoxes = [...prev];
                    newBoxes[idx] = { ...box, bbox: [newX1, newY1, newX2, newY2] };
                    return newBoxes;
                });
            };

            // threads에서 해당 box에 연결된 메시지 개수 구하기
            const boxId = box.id;
            const boxMessages = [];
            threads.map((thread) => {
                boxMessages.push(
                    ...thread.messages.filter((msg) => msg.is_deleted === false && msg.review_box?.id === boxId),
                );
            });
            const messageCount = boxMessages.length;

            return (
                <React.Fragment key={idx}>
                    <ResizableBox
                        key={idx}
                        width={width}
                        height={height}
                        minConstraints={[20, 20]}
                        maxConstraints={[curPageImageSize.width, curPageImageSize.height]}
                        onResize={handleResize}
                        axis="both"
                        // resizeHandles={["se", "e", "s", "n", "w", "ne", "nw", "sw"]}
                        resizeHandles={[]} // resize 임시 비활성화
                        style={{
                            position: "absolute",
                            left,
                            top,
                            border:
                                boxMessages.length > 0 || boxId === Constants.REVIEW_NEW_BOX_ID
                                    ? "1px solid var(--color-Key)"
                                    : "none",
                            backgroundColor: isHovered ? "rgba(0, 154, 126, 0.2)" : "transparent",
                            cursor: isHovered ? "pointer" : "default",
                        }}
                        className={isHovered ? "resizable-hovered" : ""}
                        onMouseEnter={() => setHoveredBoxIdx(idx)}
                        onMouseLeave={() => setHoveredBoxIdx(null)}
                        onResizeStart={() => setIsResizing(true)}
                        onResizeStop={() => {
                            setTimeout(() => setIsResizing(false), 0);
                            resizeBoxOnServer(boxes[idx], idx);
                        }}
                        onClick={async (e) => {
                            e.stopPropagation();
                            if (isResizing) return;

                            const box_image = await getBoxImageFromPageImage(box.data.bbox);
                            setSelectedBox({
                                ...box,
                                box_image: box_image,
                            });
                        }}
                    />
                    {isHovered && (
                        <HightlightBoxButtonGroup
                            right={buttonGroupRight}
                            top={buttonGroupTop}
                            onMouseEnter={() => setHoveredBoxIdx(idx)}
                            onMouseLeave={() => setHoveredBoxIdx(null)}
                        >
                            <HighlightBoxButtonText
                                onClick={(e) => {
                                    setHoveredBoxIdx(null);
                                    setSearchBox(box);
                                }}
                            >
                                {messageCount} messages
                            </HighlightBoxButtonText>
                        </HightlightBoxButtonGroup>
                    )}
                </React.Fragment>
            );
        });

    const getBoxImageFromPageImage = async (bbox) => {
        // pageImg가 없거나 이미지 ref가 없으면 null 반환
        if (!orgPageImage || !imgRef.current || !bbox) return null;

        // bbox 좌표 계산 (원본 기준)
        const [x1, y1, x2, y2] = bbox;
        const sx = Math.round(x1 * orgPageImageSize.width);
        const sy = Math.round(y1 * orgPageImageSize.height);
        const sw = Math.round((x2 - x1) * orgPageImageSize.width);
        const sh = Math.round((y2 - y1) * orgPageImageSize.height);

        // 원본 이미지 생성
        const image = new window.Image();
        image.src = orgPageImage;

        // 이미지 로드 후 canvas에 그리기
        return new Promise((resolve) => {
            image.onload = () => {
                const cropCanvas = document.createElement("canvas");
                cropCanvas.width = sw;
                cropCanvas.height = sh;
                const cropCtx = cropCanvas.getContext("2d");
                cropCtx.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);

                resolve({
                    base64: cropCanvas.toDataURL("image/png"),
                    width: sw,
                    height: sh,
                });
            };
            image.onerror = () => resolve(null);
        });
    };

    // 리뷰 박스 강조 처리
    const renderDimLayer = () => {
        // searchBox 강조
        if (!searchBox || curPage !== searchBox.page) return null;
        const [x1, y1, x2, y2] = searchBox.data.bbox;
        const left = x1 * curPageImageSize.width;
        const top = y1 * curPageImageSize.height;
        const width = (x2 - x1) * curPageImageSize.width;
        const height = (y2 - y1) * curPageImageSize.height;
        return (
            <ActiveDimLayer width={curPageImageSize.width} height={curPageImageSize.height}>
                <DimOverlay
                    onClick={() => {
                        setSearchBox(null);
                    }}
                />
                <HighlightBox left={left} top={top} width={width} height={height} />
            </ActiveDimLayer>
        );
    };

    // region: 새로운 박스 드래그 처리
    const [dragStart4NewBox, setDragStart4NewBox] = useState(null);
    const [dragBox4NewBox, setDragBox4NewBox] = useState(null);

    const handleMouseDown4NewBox = (e) => {
        // 기존 임시 박스가 있다면 삭제
        setSelectedBox(null);

        // 이미지 기준 좌표 계산
        const rect = imgRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        setDragStart4NewBox({ x, y });
        setDragBox4NewBox(null);
    };

    const handleMouseMove4NewBox = (e) => {
        if (!dragStart4NewBox) return;
        const rect = imgRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        setDragBox4NewBox({
            x1: Math.min(dragStart4NewBox.x, x),
            y1: Math.min(dragStart4NewBox.y, y),
            x2: Math.max(dragStart4NewBox.x, x),
            y2: Math.max(dragStart4NewBox.y, y),
        });
    };

    const handleMouseUp4NewBox = async (e) => {
        if (!dragBox4NewBox) {
            setDragStart4NewBox(null);
            return;
        }
        // 최소 크기 제한 (1% 미만이면 무시)
        if (
            Math.abs(dragBox4NewBox.x2 - dragBox4NewBox.x1) < 0.01 ||
            Math.abs(dragBox4NewBox.y2 - dragBox4NewBox.y1) < 0.01
        ) {
            setDragStart4NewBox(null);
            setDragBox4NewBox(null);
            return;
        }

        // 새 박스 추가
        try {
            const newBox = {
                id: Constants.REVIEW_NEW_BOX_ID,
                page: curPage,
                data: {
                    label: "Label",
                    bbox: [dragBox4NewBox.x1, dragBox4NewBox.y1, dragBox4NewBox.x2, dragBox4NewBox.y2],
                },
            };
            setBoxes([...boxes, { ...newBox }]);
            const box_image = await getBoxImageFromPageImage(newBox.data.bbox);
            setSelectedBox({
                ...newBox,
                box_image: box_image,
            });

            setDragStart4NewBox(null);
            setDragBox4NewBox(null);
        } catch (e) {
            alert("박스 추가 실패: " + e);
        }
    };

    const clearNewBoxes = () => {
        setBoxes([...boxes.filter((box) => box.id !== Constants.REVIEW_NEW_BOX_ID)]);
    };
    useEffect(() => {
        if (selectedBox === null) {
            clearNewBoxes();
        }
    }, [selectedBox]);

    const renderNewBoxes = () => {
        if (dragBox4NewBox) {
            return (
                <div
                    style={{
                        position: "absolute",
                        left: dragBox4NewBox.x1 * curPageImageSize.width,
                        top: dragBox4NewBox.y1 * curPageImageSize.height,
                        width: (dragBox4NewBox.x2 - dragBox4NewBox.x1) * curPageImageSize.width,
                        height: (dragBox4NewBox.y2 - dragBox4NewBox.y1) * curPageImageSize.height,
                        border: "1px dashed var(--color-Key)",
                        background: "rgba(0, 154, 126, 0.2)",
                        pointerEvents: "none",
                        zIndex: 999,
                    }}
                />
            );
        }
    };

    // region: 웹소켓 훅
    const {
        threads,
        selectedThread,
        setSelectedThread,
        profiles,
        streamingMessageIds,
        autoScrollToEnd,
        setAutoScrollToEnd,
        pdfIndexingInfo,
        chatStart,
        chatStop,
        chatDelete,
        threadUpdateTitle,
        myProfileId,
    } = usePDFChatWebSocket({
        reference_uuid: reference.uuid,
        user,
        connectionDependencies: [reference.uuid, userQuery.data, reviewPageQuery.data],
        onFileDeleted: () => {
            referenceQuery.refetch();
            navigate(`/${space_uuid}/reference/${reference_no}`, { replace: true });
        },
        onCreateReviewBox: (review_box_data) => {
            if (review_box_data.page === curPage) {
                clearNewBoxes();
                setBoxes([...boxes, { ...review_box_data }]);
            }
        },
        onStreamStart: (streaming_message_id) => {
            setFocusedMessageId(streaming_message_id);
        },
    });

    // region: 새 창으로 리뷰 진입 처리
    const [pendingThreadId, setPendingThreadId] = useState(null);
    const [pendingMessageId, setPendingMessageId] = useState(null);
    const [focusedMessageId, setFocusedMessageId] = useState(null);
    useEffect(() => {
        const receiveMessage = (e) => {
            // 보안 체크: origin 일치해야 처리
            if (e.origin !== window.location.origin) return;
            if (e.data.__channel !== Constants.REVIEW_MESSAGE_CHANNEL) return;

            const { payload } = e.data;
            if (payload.page) setCurPage(payload.page);
            if (payload.thread_id) setPendingThreadId(payload.thread_id);
            if (payload.message_id) setPendingMessageId(payload.message_id);
        };

        window.addEventListener("message", receiveMessage);
        if (window.opener) {
            window.opener.postMessage({ __channel: Constants.REVIEW_MESSAGE_CHANNEL }, window.location.origin);
        }

        return () => window.removeEventListener("message", receiveMessage);
    }, []);

    useEffect(() => {
        if (!pendingThreadId || !threads || !threads.length) return;
        const found = threads.find((t) => t.id === pendingThreadId);
        if (found) {
            setSelectedThread(found);
            setFocusedMessageId(pendingMessageId);
            setPendingThreadId(null);
            setPendingMessageId(null);
        }
    }, [pendingThreadId, threads]);

    // region: 마크업
    return (
        <PageLayout>
            <HtmlHead title={reference?.display_name} />
            <PDFChatPageHeader reference={reference} />

            <PDFChatPageLayout
                main={
                    <PdfReaderContainer>
                        <ResizableHandleStyle />
                        <PdfReaderToolbar>
                            <PageMoveControlContainer>
                                <PageMoveButton
                                    onClick={() => handleInputPageChanged(curPage - 1)}
                                    disabled={curPage <= 1}
                                >
                                    ◀
                                </PageMoveButton>
                                <PageMoveInputControl
                                    type="number"
                                    min={1}
                                    value={inputPage}
                                    onChange={(e) => setInputPage(Number(e.target.value))}
                                    onBlur={() => {
                                        if (inputPage !== curPage) handleInputPageChanged(inputPage);
                                        else setInputPage(curPage);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && inputPage !== curPage)
                                            handleInputPageChanged(inputPage);
                                    }}
                                />
                                <PageMoveText>/</PageMoveText>
                                <PageMoveText>{maxPage}</PageMoveText>
                                <PageMoveButton
                                    onClick={() => handleInputPageChanged(curPage + 1)}
                                    disabled={curPage >= maxPage}
                                >
                                    ▶
                                </PageMoveButton>
                            </PageMoveControlContainer>
                        </PdfReaderToolbar>
                        <PageImageContainer
                            ref={scrollContainerRef}
                            onMouseDown={handleMouseDown4NewBox}
                            onMouseMove={handleMouseMove4NewBox}
                            onMouseUp={handleMouseUp4NewBox}
                            onWheel={handleWheelOnContainer}
                        >
                            <PageImage
                                ref={imgRef}
                                src={orgPageImage}
                                alt=""
                                draggable={false}
                                onLoad={handlePageImageLoad}
                            />
                            {renderBoxes()}
                            {renderNewBoxes()}
                            {renderDimLayer()}
                        </PageImageContainer>
                    </PdfReaderContainer>
                }
                sidebar={
                    <PDFChatSidebar
                        user={user}
                        myProfileId={myProfileId}
                        reference={reference}
                        threads={threads}
                        selectedThread={selectedThread}
                        setSelectedThread={setSelectedThread}
                        profiles={profiles}
                        setCurPage={setCurPage}
                        selectedBox={selectedBox}
                        setSelectedBox={setSelectedBox}
                        searchBox={searchBox}
                        setSearchBox={setSearchBox}
                        chatStart={chatStart}
                        chatStop={chatStop}
                        chatDelete={chatDelete}
                        threadUpdateTitle={threadUpdateTitle}
                        streamingMessageIds={streamingMessageIds}
                        autoScrollToEnd={autoScrollToEnd}
                        setAutoScrollToEnd={setAutoScrollToEnd}
                        pdfIndexingInfo={pdfIndexingInfo}
                        focusedMessageId={focusedMessageId}
                    />
                }
            ></PDFChatPageLayout>
        </PageLayout>
    );
}

export default ReferenceReviewPage;
